import { useCallback, useEffect, useRef, useState } from "react";
import { IdleTracker } from "@/lib/idle-logout/idle-tracker";

/** Shown 60s before logout with a "Är du kvar?" countdown, per GRA-80. */
export const IDLE_LOGOUT_WARNING_MS = 60_000;

export type IdleLogoutStatus = "active" | "warning";

export interface UseIdleLogoutOptions {
  /** Minutes of inactivity before logout, or `null`/`undefined` to disable tracking entirely. */
  timeoutMinutes: number | null | undefined;
  /** Called once when the idle timeout is reached. */
  onTimeout: () => void;
}

export interface UseIdleLogoutResult {
  status: IdleLogoutStatus;
  /** Milliseconds remaining until logout; only meaningful while `status === "warning"`. */
  remainingMs: number;
  /** Record activity now and dismiss the warning — the "Fortsätt" button. */
  extend: () => void;
}

/**
 * Wires an {@link IdleTracker} into React state. Disabled (no listeners, no
 * timers) whenever `timeoutMinutes` is `null`/`undefined`, so the business
 * setting being off has zero runtime cost — matches the "with the setting
 * off, nothing changes for anyone" requirement.
 */
export function useIdleLogout({
  timeoutMinutes,
  onTimeout,
}: UseIdleLogoutOptions): UseIdleLogoutResult {
  const [status, setStatus] = useState<IdleLogoutStatus>("active");
  const [remainingMs, setRemainingMs] = useState(IDLE_LOGOUT_WARNING_MS);
  const trackerRef = useRef<IdleTracker | null>(null);

  // Kept in a ref so a timer tick never fires a stale closure without forcing
  // the effect below to re-run (and thus the tracker to restart) on every
  // render. Updated in its own effect (not during render) per the
  // react-hooks/refs rule.
  const onTimeoutRef = useRef(onTimeout);
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });

  useEffect(() => {
    setStatus("active");
    if (!timeoutMinutes || timeoutMinutes <= 0) {
      return;
    }

    const tracker = new IdleTracker({
      timeoutMs: timeoutMinutes * 60_000,
      warningMs: IDLE_LOGOUT_WARNING_MS,
      onWarning: (remaining) => {
        setStatus("warning");
        setRemainingMs(remaining);
      },
      onTimeout: () => {
        onTimeoutRef.current();
      },
    });
    trackerRef.current = tracker;
    tracker.start();

    return () => {
      tracker.stop();
      trackerRef.current = null;
    };
  }, [timeoutMinutes]);

  const extend = useCallback(() => {
    trackerRef.current?.extend();
    setStatus("active");
    setRemainingMs(IDLE_LOGOUT_WARNING_MS);
  }, []);

  return { status, remainingMs, extend };
}
